import React, { useState } from 'react';
import { Building2, Check, CheckCircle2, ChevronLeft, Clock3, Mail, MessageCircle, Phone, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { SubscriptionPlan } from '../../data/subscriptionPlans';
import { useTheme } from '../../contexts/ThemeContext';
type CancellationStep = 'reason' | 'details' | 'confirm' | 'forwardingCheck' | 'forwardingType' | 'cancelled';
type ForwardingNumberType = 'mobile' | 'landline';

/** Stable ids so reasons can be counted and compared over time. */
export type CancellationReasonId = 'too_expensive' | 'low_call_volume' | 'forwarding_issues' | 'ai_quality' | 'missing_feature' | 'switching_service' | 'temporary_need' | 'other';
export type ReturnLikelihood = 'likely' | 'maybe' | 'unlikely';
export interface CancellationFeedback {
  reason: CancellationReasonId | null;
  details: string;
  returnLikelihood: ReturnLikelihood | null;
  isTrial: boolean;
  planId: string;
}
interface CancellationRetentionSheetProps {
  plan: SubscriptionPlan;
  minutesUsed: number;
  includedMinutes: number;
  isTrial: boolean;
  accessEndsLabel: string;
  onClose: () => void;
  onNavigate?: (screen: string) => void;
  onGuideForwardingDeactivation: (numberType: ForwardingNumberType) => void;
  onCancelSubscription: () => void;
  onKeepSubscription: (message: string) => void;
  /** Receives the answers once the user confirms cancellation. */
  onSubmitFeedback?: (feedback: CancellationFeedback) => void;
  /** Type of the connected number, when known — skips asking the user. */
  connectedNumberType?: ForwardingNumberType | null;
  /** false when every forwarding rule is already off — skips the forwarding check. */
  forwardingActive?: boolean;
}
const SUPPORT_EMAIL = 'support@vexu.ai';
const REASONS: Array<{
  id: CancellationReasonId;
  label: string;
  prompt: string;
  placeholder: string;
}> = [{
  id: 'too_expensive',
  label: 'It’s too expensive',
  prompt: 'What price would feel right to you?',
  placeholder: 'e.g. I’d pay around…'
}, {
  id: 'low_call_volume',
  label: 'I don’t get enough calls',
  prompt: 'Roughly how many calls did Vexu answer for you?',
  placeholder: 'e.g. only a few a week'
}, {
  id: 'forwarding_issues',
  label: 'Call forwarding didn’t work',
  prompt: 'What went wrong with forwarding?',
  placeholder: 'e.g. my carrier code failed, calls still rang…'
}, {
  id: 'ai_quality',
  label: 'The AI didn’t handle calls well',
  prompt: 'What did the AI get wrong?',
  placeholder: 'e.g. misunderstood callers, wrong language…'
}, {
  id: 'missing_feature',
  label: 'It’s missing a feature I need',
  prompt: 'Which feature do you need?',
  placeholder: 'e.g. CRM sync, SMS replies…'
}, {
  id: 'switching_service',
  label: 'I’m switching to another service',
  prompt: 'Which service are you moving to, and why?',
  placeholder: 'e.g. another AI receptionist…'
}, {
  id: 'temporary_need',
  label: 'I only needed it for a while',
  prompt: 'Anything we should know for next time?',
  placeholder: 'Optional'
}, {
  id: 'other',
  label: 'Something else',
  prompt: 'Tell us what led to this decision.',
  placeholder: 'Your reason…'
}];
const RETURN_OPTIONS: Array<{
  id: ReturnLikelihood;
  label: string;
}> = [{
  id: 'likely',
  label: 'Likely'
}, {
  id: 'maybe',
  label: 'Maybe'
}, {
  id: 'unlikely',
  label: 'Unlikely'
}];
const BACK: Partial<Record<CancellationStep, CancellationStep>> = {
  details: 'reason',
  confirm: 'details',
  forwardingCheck: 'confirm',
  forwardingType: 'forwardingCheck'
};
export function CancellationRetentionSheet({
  plan,
  isTrial,
  accessEndsLabel,
  onClose,
  onNavigate,
  onGuideForwardingDeactivation,
  onCancelSubscription,
  onSubmitFeedback,
  connectedNumberType = null,
  forwardingActive
}: CancellationRetentionSheetProps) {
  const {
    isLight
  } = useTheme();
  const [step, setStep] = useState<CancellationStep>('reason');
  const [reason, setReason] = useState<CancellationReasonId | null>(null);
  const [details, setDetails] = useState('');
  const [returnLikelihood, setReturnLikelihood] = useState<ReturnLikelihood | null>(null);
  const [detailsError, setDetailsError] = useState('');
  const selectedReason = REASONS.find(r => r.id === reason);
  const strong = isLight ? 'text-slate-900' : 'text-white';
  const muted = isLight ? 'text-slate-500' : 'text-zinc-400';
  const backTarget = BACK[step];
  const confirmBack: CancellationStep = reason ? 'details' : 'reason';
  const continueFromDetails = () => {
    if (reason === 'other' && details.trim().length < 3) {
      setDetailsError('Please share a few words so we understand.');
      return;
    }
    setDetailsError('');
    setStep('confirm');
  };
  const confirmCancellation = () => {
    onSubmitFeedback?.({
      reason,
      details: details.trim(),
      returnLikelihood,
      isTrial,
      planId: plan.id
    });
    // Nothing forwards to Vexu any more — no need to ask about forwarding.
    setStep(forwardingActive === false ? 'cancelled' : 'forwardingCheck');
  };

  // Use the same turn-off steps as Home; only ask for the number type if we don't know it.
  const guideTurnOff = () => {
    if (connectedNumberType) onGuideForwardingDeactivation(connectedNumberType);else setStep('forwardingType');
  };
  const openSupportChat = () => {
    onClose();
    onNavigate?.('support');
  };
  const emailHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Before I cancel my Vexu subscription')}&body=${encodeURIComponent([selectedReason ? `Reason: ${selectedReason.label}` : '', details.trim() ? `Details: ${details.trim()}` : '', ''].filter(Boolean).join('\n'))}`;
  const SupportCard = () => <div className={`mt-5 rounded-2xl border p-4 ${isLight ? 'border-slate-200 bg-slate-50' : 'border-white/10 bg-white/[0.04]'}`} data-id="element-259" data-mp="2ik90m601kzuf">
      <p className={`text-sm font-bold ${strong}`} data-id="element-260" data-mp="2nik5s3fe6m6b">Can we fix this together?</p>
      <p className={`mt-0.5 text-xs leading-relaxed ${muted}`} data-id="element-261" data-mp="o7svx9mw9tro">Our team usually replies within a few hours.</p>
      <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-262" data-mp="t6lxq4stjwc0">
        <button type="button" onClick={openSupportChat} className={`flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border py-2.5 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'border-slate-200 bg-white text-slate-800 hover:border-slate-300' : 'border-white/10 bg-white/5 text-white hover:border-white/20'}`} data-id="element-263" data-mp="1wgqf43krv371">
          <MessageCircle className={`h-3.5 w-3.5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-264" data-mp="2feb2f67w1tpj" />
          Chat with us
        </button>
        <a href={emailHref} className={`flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border py-2.5 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'border-slate-200 bg-white text-slate-800 hover:border-slate-300' : 'border-white/10 bg-white/5 text-white hover:border-white/20'}`} data-id="element-265" data-mp="2aeevay3w9m5r">
          <Mail className={`h-3.5 w-3.5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-266" data-mp="2t566843j5km9" />
          Email us
        </a>
      </div>
      <p className={`mt-2.5 text-center text-[11px] ${muted}`} data-id="element-267" data-mp="3hsf5t59kew9a">{SUPPORT_EMAIL}</p>
    </div>;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[60] flex items-end p-4" role="presentation" data-id="element-268" data-mp="32vebctzzon74">
      <button type="button" aria-label="Close cancellation" onClick={onClose} className="absolute inset-0 bg-black/50 backdrop-blur-sm" data-id="element-269" data-mp="2u21b16zlijb0" />
      <motion.section role="dialog" aria-modal="true" aria-labelledby="cancellation-title" initial={{
      y: 120
    }} animate={{
      y: 0
    }} exit={{
      y: 120
    }} transition={{
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} className={`relative mx-auto w-full max-w-sm overflow-hidden rounded-3xl shadow-2xl ${isLight ? 'bg-white' : 'border border-white/10 bg-[#1c1c1e]'}`} data-id="element-270" data-mp="2k4dgoi5wjdtd">
        <div className={`flex items-center justify-between border-b px-5 py-4 ${isLight ? 'border-slate-100' : 'border-white/10'}`} data-id="element-271" data-mp="2vji4e9bherfi">
          {backTarget || step === 'confirm' ? <button type="button" onClick={() => setStep(step === 'confirm' ? confirmBack : backTarget as CancellationStep)} aria-label="Back" className={`-ml-2 rounded-full p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'text-slate-500 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/10'}`} data-id="element-272" data-mp="2llu6xbmhr5tn">
              <ChevronLeft className="h-5 w-5" data-id="element-273" data-mp="22v2ug177izb1" />
            </button> : <span className="w-9" data-id="element-274" data-mp="1xxb0l1n05347" />}
          <p className={`text-xs font-bold tracking-wide ${muted}`} data-id="element-275" data-mp="1xym3z0cjc8us">Cancel subscription</p>
          <button type="button" onClick={onClose} aria-label="Close" className={`rounded-full p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/10'}`} data-id="element-276" data-mp="1sx6ix8acy2ce">
            <X className="h-5 w-5" data-id="element-277" data-mp="8hqldajm435p" />
          </button>
        </div>

        <div className="max-h-[72vh] overflow-y-auto px-6 py-6" data-id="element-278" data-mp="dg1v7l9wqdnx">
          <AnimatePresence mode="wait" data-id="element-279" data-mp="idlb0pm96yhz">
            {step === 'reason' && <motion.div key="reason" {...sheetMotion} data-id="element-280" data-mp="ibh37wl8hfcf">
                <h2 id="cancellation-title" className={`text-2xl font-bold tracking-tight ${strong}`} data-id="element-281" data-mp="3gws7uizzvoyl">
                  Why are you cancelling?
                </h2>
                <p className={`mt-2 text-sm leading-relaxed ${muted}`} data-id="element-282" data-mp="3qt4vp08uz8lf">
                  Pick the main reason. Your answer helps us improve Vexu.
                </p>
                <div role="radiogroup" aria-label="Main reason" className="mt-5 space-y-1.5" data-id="element-283" data-mp="3vm6gfovqy2cx">
                  {REASONS.map(item => {
                const selected = reason === item.id;
                return <button key={item.id} type="button" role="radio" aria-checked={selected} onClick={() => {
                  if (item.id !== reason) setDetails('');
                  setReason(item.id);
                  setDetailsError('');
                }} className={`flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${selected ? isLight ? 'border-vexu bg-vexu/[0.05]' : 'border-vexu bg-vexu/10' : isLight ? 'border-slate-200 hover:border-slate-300' : 'border-white/10 hover:border-white/20'}`} data-id="element-284" data-mp="2cv9muthzu1ax">
                        <span className={`grid h-[18px] w-[18px] flex-shrink-0 place-items-center rounded-full border-2 transition-colors duration-150 ${selected ? 'border-vexu bg-vexu text-white' : isLight ? 'border-slate-300' : 'border-white/25'}`} aria-hidden data-id="element-285" data-mp="1bczerxb0lj0b">
                          {selected && <Check className="h-2.5 w-2.5" strokeWidth={3.5} data-id="element-286" data-mp="3kmgr6j0zm97r" />}
                        </span>
                        <span className={`text-sm font-semibold ${strong}`} data-id="element-287" data-mp="3fnm4v73ta2iw">{item.label}</span>
                      </button>;
              })}
                </div>
                <button type="button" disabled={!reason} onClick={() => setStep('details')} className="mt-5 w-full rounded-xl bg-vexu py-3.5 text-sm font-bold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu focus-visible:ring-offset-2" data-id="element-288" data-mp="35xkkapao328q">
                  Continue
                </button>
                <button type="button" onClick={() => setStep('confirm')} className={`mt-2 w-full py-2.5 text-xs font-semibold ${isLight ? 'text-slate-500 hover:text-slate-800' : 'text-zinc-400 hover:text-white'}`} data-id="element-289" data-mp="2n0roala47nap">
                  Skip and continue to cancellation
                </button>
              </motion.div>}

            {step === 'details' && selectedReason && <motion.div key="details" {...sheetMotion} data-id="element-290" data-mp="2w2loiakukeks">
                <p className={`text-xs font-semibold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-291" data-mp="310ng5jwc9m7v">{selectedReason.label}</p>
                <h2 id="cancellation-title" className={`mt-1.5 text-xl font-bold tracking-tight ${strong}`} data-id="element-292" data-mp="2na1wvu2z3a9g">
                  {selectedReason.prompt}
                </h2>
                <label htmlFor="cancellation-details" className="sr-only" data-id="element-293" data-mp="3f0g6vf7v82fu">
                  Details
                </label>
                <textarea id="cancellation-details" value={details} onChange={e => {
              setDetails(e.target.value.slice(0, 500));
              setDetailsError('');
            }} placeholder={selectedReason.placeholder} rows={3} maxLength={500} aria-describedby="cancellation-details-help" className={`mt-4 w-full resize-none rounded-2xl border p-3.5 text-sm leading-relaxed outline-none transition-colors ${detailsError ? isLight ? 'border-red-400 bg-red-50/40 text-slate-900' : 'border-red-500/50 bg-red-500/5 text-white' : isLight ? 'border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:border-vexu focus:ring-2 focus:ring-vexu/15' : 'border-white/10 bg-white/5 text-white placeholder:text-zinc-500 focus:border-vexu'}`} data-id="element-294" data-mp="3jrenkfwrn2jm" />
                <p id="cancellation-details-help" className={`mt-1.5 flex justify-between text-[11px] ${detailsError ? 'text-red-500' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-295" data-mp="2aotjqputecmx">
                  <span data-id="element-296" data-mp="k99eah88n37j">{detailsError || (reason === 'other' ? 'Required' : 'Optional')}</span>
                  <span data-id="element-297" data-mp="ob4hxsdn5soh">{details.length}/500</span>
                </p>

                <p className={`mt-5 text-sm font-bold ${strong}`} data-id="element-298" data-mp="od8suu9yjry9">Would you use Vexu again in the future?</p>
                <div role="radiogroup" aria-label="Would you use Vexu again" className="mt-2.5 grid grid-cols-3 gap-2" data-id="element-299" data-mp="t7utq3iey243">
                  {RETURN_OPTIONS.map(o => {
                const selected = returnLikelihood === o.id;
                return <button key={o.id} type="button" role="radio" aria-checked={selected} onClick={() => setReturnLikelihood(selected ? null : o.id)} className={`rounded-xl border py-2.5 text-xs font-bold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${selected ? isLight ? 'border-vexu bg-vexu/[0.06] text-vexu-700' : 'border-vexu bg-vexu/10 text-vexu' : isLight ? 'border-slate-200 text-slate-600 hover:border-slate-300' : 'border-white/10 text-zinc-300 hover:border-white/20'}`} data-id="element-300" data-mp="180esyt2fzpg9">
                        {o.label}
                      </button>;
              })}
                </div>

                <SupportCard data-id="element-301" data-mp="2ev5k2z24nc2b" />

                <button type="button" onClick={continueFromDetails} className="mt-5 w-full rounded-xl bg-vexu py-3.5 text-sm font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu focus-visible:ring-offset-2" data-id="element-302" data-mp="29tq2jgz5rnoo">
                  Continue to cancellation
                </button>
              </motion.div>}

            {step === 'confirm' && <motion.div key="confirm" {...sheetMotion} data-id="element-303" data-mp="2sr99mf222w8g">
                <div className={`grid h-12 w-12 place-items-center rounded-2xl ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-300'}`} data-id="element-304" data-mp="qwv0hsll359c">
                  <Clock3 className="h-6 w-6" data-id="element-305" data-mp="h60empc927of" />
                </div>
                <h2 id="cancellation-title" className={`mt-5 text-2xl font-bold tracking-tight ${strong}`} data-id="element-306" data-mp="h3o0yodiyjh6">
                  Confirm cancellation
                </h2>
                <p className={`mt-3 text-sm leading-relaxed ${muted}`} data-id="element-307" data-mp="849ahoxcy8wo">
                  {isTrial ? `Cancel before ${accessEndsLabel} and your card will not be charged.` : `Your Vexu ${plan.name} plan stays active until ${accessEndsLabel}. There will be no further renewals.`}{' '}
                  After that, your AI assistant stops answering calls.
                </p>
                <SupportCard data-id="element-308" data-mp="1slfafpsq2thy" />
                <button type="button" onClick={confirmCancellation} className={`mt-5 w-full rounded-xl border py-3.5 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${isLight ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-red-500/30 text-red-400 hover:bg-red-500/10'}`} data-id="element-309" data-mp="1sj2svdqvml5f">
                  Confirm cancellation
                </button>
                <button type="button" onClick={onClose} className={`mt-2 w-full py-3 text-sm font-semibold ${isLight ? 'text-slate-600 hover:text-slate-900' : 'text-zinc-300 hover:text-white'}`} data-id="element-310" data-mp="3mzjfhsdttpe3">
                  Keep my plan
                </button>
              </motion.div>}

            {step === 'forwardingCheck' && <motion.div key="forwarding-check" {...sheetMotion} data-id="element-311" data-mp="9n0wen0iym6n">
                <div className={`grid h-12 w-12 place-items-center rounded-2xl ${isLight ? 'bg-amber-50 text-amber-600' : 'bg-amber-500/10 text-amber-400'}`} data-id="element-312" data-mp="2gdqltq5w5p24">
                  <Phone className="h-6 w-6" data-id="element-313" data-mp="1pkf35gmkhcbm" />
                </div>
                <h2 id="cancellation-title" className={`mt-5 text-2xl font-bold tracking-tight ${strong}`} data-id="element-314" data-mp="1pk5afla50r28">
                  Have you turned off call forwarding?
                </h2>
                <p className={`mt-3 text-sm leading-relaxed ${muted}`} data-id="element-315" data-mp="1zbjg6asomn3t">
                  Once Vexu stops answering, calls that still forward to it may not connect. We’ll walk you through the same
                  steps you used to set it up, then finish cancelling.
                </p>
                <button type="button" onClick={guideTurnOff} className="mt-6 w-full rounded-xl bg-vexu py-3.5 text-sm font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu focus-visible:ring-offset-2" data-id="element-316" data-mp="1blp3bt8d4yjk">
                  Help me turn off forwarding
                </button>
                <button type="button" onClick={() => setStep('cancelled')} className={`mt-2 w-full py-3 text-sm font-semibold ${isLight ? 'text-slate-600 hover:text-slate-900' : 'text-zinc-300 hover:text-white'}`} data-id="element-317" data-mp="2wxh26i69taki">
                  It’s already turned off
                </button>
              </motion.div>}

            {step === 'forwardingType' && <motion.div key="forwarding-type" {...sheetMotion} data-id="element-318" data-mp="6l7nfra9p5p1">
                <h2 id="cancellation-title" className={`text-2xl font-bold tracking-tight ${strong}`} data-id="element-319" data-mp="1mwedirsqz7v">
                  Which number forwards your calls?
                </h2>
                <p className={`mt-3 text-sm leading-relaxed ${muted}`} data-id="element-320" data-mp="3or3vs2zzhp2u">
                  We’ll show the right steps, then finish cancelling your subscription.
                </p>
                <div className="mt-5 space-y-2.5" data-id="element-321" data-mp="3julx9kv8ymyy">
                  {[{
                type: 'mobile' as const,
                icon: Phone,
                title: 'Mobile number',
                desc: 'Turn off forwarding from your phone.'
              }, {
                type: 'landline' as const,
                icon: Building2,
                title: 'Landline number',
                desc: 'Turn forwarding off from your handset.'
              }].map(({
                type,
                icon: Icon,
                title,
                desc
              }) => <button key={type} type="button" onClick={() => onGuideForwardingDeactivation(type)} className={`w-full rounded-2xl border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'border-slate-200 hover:border-slate-300 hover:bg-slate-50' : 'border-white/10 hover:border-white/20 hover:bg-white/5'}`} data-id="element-322" data-mp="204w2l26x7xxa">
                      <span className="flex items-center gap-3" data-id="element-323" data-mp="1c7895xgfp0fd">
                        <span className={`grid h-10 w-10 place-items-center rounded-xl ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-300'}`} data-id="element-324" data-mp="1c91gleyvfoof">
                          <Icon className="h-5 w-5" data-id="element-325" data-mp="36kzrrmgqqumy" />
                        </span>
                        <span data-id="element-326" data-mp="3f2ry51j48bj2">
                          <span className={`block text-sm font-bold ${strong}`} data-id="element-327" data-mp="3f1gtkzhuizqw">{title}</span>
                          <span className={`mt-0.5 block text-xs ${muted}`} data-id="element-328" data-mp="3j2seirprywsr">{desc}</span>
                        </span>
                      </span>
                    </button>)}
                </div>
              </motion.div>}

            {step === 'cancelled' && <motion.div key="cancelled" {...sheetMotion} className="text-center" data-id="element-329" data-mp="2w4fn1a8n8rku">
                <div className={`mx-auto grid h-14 w-14 place-items-center rounded-full ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-300'}`} data-id="element-330" data-mp="3047o71vpjvg6">
                  <CheckCircle2 className="h-7 w-7" data-id="element-331" data-mp="356f2vz7x9pm7" />
                </div>
                <h2 id="cancellation-title" className={`mt-5 text-2xl font-bold tracking-tight ${strong}`} data-id="element-332" data-mp="3a4gpuv8gy92y">
                  Your cancellation is set.
                </h2>
                <p className={`mt-3 text-sm leading-relaxed ${muted}`} data-id="element-333" data-mp="pbv96issregj">
                  {isTrial ? 'You will not be charged when your trial ends.' : `You’ll keep access through ${accessEndsLabel}.`}{' '}
                  Thanks for your feedback.
                </p>
                <p className={`mt-2 text-xs ${muted}`} data-id="element-334" data-mp="9moz6v04ndlj">
                  Questions? <a href={`mailto:${SUPPORT_EMAIL}`} className={`font-semibold underline underline-offset-2 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-335" data-mp="9kvwfqsmxdit">{SUPPORT_EMAIL}</a>
                </p>
                <button type="button" onClick={onCancelSubscription} className="mt-6 w-full rounded-xl bg-slate-900 py-3.5 text-sm font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500" data-id="element-336" data-mp="3d1kvkrpw1kyg">
                  Done
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.section>
    </motion.div>;
}
const sheetMotion = {
  initial: {
    opacity: 0,
    x: 12
  },
  animate: {
    opacity: 1,
    x: 0
  },
  exit: {
    opacity: 0,
    x: -12
  },
  transition: {
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }
};